import { useEffect } from "react";
import { API_BASE_URL } from "@/lib/api";

export interface AdminChatMessage {
    id: string;
    role: 'assistant' | 'user';
    content: string;
    createdAt: string;
}

interface UseChatSseOptions {
    sessionId: string | null | undefined;
    onAdminMessage: (message: AdminChatMessage) => void;
    enabled?: boolean;
}

/**
 * Subscribes to server-sent admin replies for a web chat session.
 *
 * Backend pushes `{ type: 'admin_message', message: ... }` whenever admin
 * replies via the admin panel. EventSource handles reconnects natively.
 */
export function useChatSse({ sessionId, onAdminMessage, enabled = true }: UseChatSseOptions) {
    useEffect(() => {
        if (!enabled || !sessionId) return;

        const url = `${API_BASE_URL}/chatbot/web/session/${encodeURIComponent(sessionId)}/events`;
        const source = new EventSource(url);

        source.onmessage = (event) => {
            try {
                const payload = JSON.parse(event.data);
                if (payload?.type === 'admin_message' && payload.message) {
                    onAdminMessage(payload.message as AdminChatMessage);
                }
            } catch (err) {
                console.warn('[chat-sse] parse error', err);
            }
        };

        source.onerror = () => {
            // Browser will auto-reconnect. Nothing to do.
        };

        return () => {
            source.close();
        };
    }, [sessionId, enabled, onAdminMessage]);
}
